import React, { useState } from 'react';
import { X, User, Users, CheckCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { RecruiterSelector } from './RecruiterSelector';

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (recruiterId: string, openToAll: boolean, reason?: string) => void;
  recruiters?: Array<{ id: string; name: string; email: string }>;
  candidateName?: string;
  isLoading?: boolean;
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  recruiters = [],
  candidateName,
  isLoading = false
}) => {
  const [selectedRecruiterId, setSelectedRecruiterId] = useState<string>('');
  const [openToAll, setOpenToAll] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (openToAll || selectedRecruiterId) {
      onSubmit(selectedRecruiterId, openToAll, reason || undefined);
      // Reset form
      setSelectedRecruiterId('');
      setOpenToAll(false);
      setReason('');
    }
  };

  const handleClose = () => {
    setSelectedRecruiterId('');
    setOpenToAll(false);
    setReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card rounded-2xl border border-glass-border/40 shadow-2xl p-6 animate-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-enterprise-primary">
                  Assign Candidate
                </h2>
                {candidateName && (
                  <p className="text-sm text-text-enterprise-tertiary">
                    {candidateName}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 hover:bg-glass-surface/50 rounded-lg transition-colors group disabled:opacity-50"
              title="Close"
            >
              <X className="w-5 h-5 text-text-enterprise-tertiary group-hover:text-text-enterprise-primary" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Open to All Checkbox */}
            <div className="flex items-center gap-3 p-4 rounded-xl border border-glass-border/40 bg-glass-surface/30 hover:bg-glass-surface/50 transition-colors cursor-pointer" onClick={() => setOpenToAll(!openToAll)}>
              <div className={cn(
                "w-5 h-5 rounded border-2 flex items-center justify-center transition-all",
                openToAll 
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 border-transparent" 
                  : "border-glass-border/50 hover:border-primary-400"
              )}>
                {openToAll && <CheckCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-text-enterprise-primary cursor-pointer">
                  Open to All Recruiters
                </label>
                <p className="text-xs text-text-enterprise-tertiary">
                  Any recruiter can claim this candidate
                </p>
              </div>
            </div>

            {/* Or Divider */}
            {!openToAll && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-glass-border/40" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="px-2 bg-glass-surface text-text-enterprise-tertiary">
                      Or
                    </span>
                  </div>
                </div>

                {/* Recruiter Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                    Assign to Specific Recruiter
                  </label>
                  <RecruiterSelector
                    recruiters={recruiters}
                    value={selectedRecruiterId}
                    onChange={setSelectedRecruiterId}
                    placeholder="Search and select recruiter..."
                    disabled={openToAll || isLoading}
                  />
                </div>
              </>
            )}

            {/* Reason Field */}
            <div>
              <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isLoading}
                placeholder="Add a note about this assignment..."
                rows={3}
                className={cn(
                  "w-full px-4 py-2.5 rounded-xl border border-glass-border/40",
                  "bg-glass-surface/50 backdrop-blur-sm",
                  "text-text-enterprise-primary placeholder-text-enterprise-tertiary",
                  "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                  "resize-none transition-all duration-200",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-xl border border-glass-border/40",
                "bg-glass-surface/50 backdrop-blur-sm",
                "text-text-enterprise-primary font-medium",
                "hover:bg-glass-surface transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || (!openToAll && !selectedRecruiterId)}
              className={cn(
                "flex-1 px-4 py-2.5 rounded-xl font-medium",
                "bg-gradient-to-r from-blue-500 to-purple-500",
                "text-white hover:shadow-lg hover:scale-[1.02]",
                "transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-primary-500/50",
                "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
                isLoading && "opacity-70"
              )}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Assigning...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <User className="w-4 h-4" />
                  Assign
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
